/**
 * @upmind/formguard/vue2 — Vue 2 plugin for FormGuard token management
 *
 * Installs a global FormGuard plugin that fetches a JWT token and solves a
 * proof-of-work challenge on demand — only when a protected request is
 * about to fire. Token and proof are sent as headers, not in the body.
 *
 * @example
 * ```ts
 * import Vue from 'vue';
 * import axios from 'axios';
 * import FormGuardPlugin from '@upmind/formguard/vue2';
 *
 * Vue.use(FormGuardPlugin, {
 *   axios,
 *   onError: (err) => store.dispatch('toast/error', { message: err.message }),
 * });
 * ```
 *
 * @module @upmind/formguard/vue2
 */
import { TokenManager, computeRequestHash, } from "../client/index.js";
const FormGuardPlugin = {
    install(Vue, options = {}) {
        const { axios, paths = ["/oauth/access_token"], skipGrantTypes = [], onError: onPluginError, ...managerOptions } = options;
        // Reactive state via Vue.observable
        const state = Vue.observable({
            token: null,
            loading: false,
            error: null,
        });
        const manager = new TokenManager({
            ...managerOptions,
            onToken(token) {
                state.token = token;
                state.loading = false;
                state.error = null;
                managerOptions.onToken?.(token);
            },
            onError(error) {
                state.loading = false;
                state.error = error;
                onPluginError?.(error);
            },
        });
        // Axios interceptor — fetches a fresh token + solves PoW on demand,
        // computes request hash, and injects as headers.
        if (axios) {
            axios.interceptors.request.use(async (config) => {
                const url = config.url ?? "";
                const method = (config.method ?? "get").toLowerCase();
                const isProtected = paths.some((path) => url.includes(path));
                if (!isProtected || !["post", "put", "patch"].includes(method)) {
                    return config;
                }
                // Skip verification for configured grant types
                if (skipGrantTypes.length > 0 && config.data != null) {
                    const data = typeof config.data === "string"
                        ? (() => { try {
                            return JSON.parse(config.data);
                        }
                        catch {
                            return null;
                        } })()
                        : config.data;
                    if (data?.grant_type && skipGrantTypes.includes(data.grant_type)) {
                        return config;
                    }
                }
                state.loading = true;
                // Compute request hash from the serialized body
                let pathname;
                try {
                    pathname = new URL(url, config.baseURL || "http://localhost")
                        .pathname;
                }
                catch {
                    pathname = url;
                }
                const body = typeof config.data === "string"
                    ? config.data
                    : JSON.stringify(config.data ?? "");
                const contentType = config.headers?.["Content-Type"] ?? "application/json";
                const hash = await computeRequestHash(method.toUpperCase(), pathname, body, contentType);
                // Fetch token bound to this request hash
                await manager.fetchToken(hash);
                // Inject token + proof as headers
                config.headers = config.headers ?? {};
                Object.assign(config.headers, manager.getTokenHeaders());
                // Forward debug header so the worker returns verbose errors
                try {
                    const debugSecret = localStorage.getItem("formguard_debug");
                    if (debugSecret)
                        config.headers["X-FormGuard-Debug"] = debugSecret;
                }
                catch {
                    // localStorage unavailable
                }
                return config;
            });
        }
        const instance = {
            get token() {
                return state.token;
            },
            get loading() {
                return state.loading;
            },
            get error() {
                return state.error;
            },
            getTokenHeaders: () => manager.getTokenHeaders(),
            refresh: async (requestHash) => {
                state.loading = true;
                try {
                    await manager.fetchToken(requestHash);
                }
                catch {
                    // Already handled via onError callback
                }
            },
            manager,
        };
        Vue.prototype.$formGuard = instance;
    },
};
export default FormGuardPlugin;
//# sourceMappingURL=index.js.map